import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { byId, catalogue, initialState } from './data/catalog'
import { drawDiscovery, feedNeeded, fishStage, rewardSession, type DrawResult } from './domain/economy'
import { createId } from './domain/id'
import { completeSession, createSession, endSessionEarly, pauseSession, resumeSession } from './domain/timer'
import { appendSessionToTask, isGoogleConnected } from './services/googleTasks'
import { clearState, loadState, saveState } from './services/storage'
import { loadRemoteState, saveRemoteState } from './services/api'
import { useAuth } from './auth'
import type { AppSettings, AppState, GoogleTaskRef, SessionMode } from './types'

interface AppStore {
  state: AppState
  hydrated: boolean
  databaseStatus: 'loading' | 'saved' | 'saving' | 'offline'
  activeSession: AppState['sessions'][number] | undefined
  lastDraw?: DrawResult
  chooseStarter: (catalogueId: string) => void
  startSession: (title: string, minutes: number, mode: SessionMode, googleTask?: GoogleTaskRef) => void
  pauseActive: () => void
  resumeActive: () => void
  endActive: () => void
  completeActive: () => void
  adminCompleteActive: () => void
  dismissCompletion: () => void
  feedFish: (fishId: string, amount?: number) => void
  spin: () => void
  clearDraw: () => void
  togglePlacement: (itemId: string) => void
  movePlacement: (itemId: string, x: number, y: number) => void
  setBackground: (itemId: string) => void
  arrangeForMe: () => void
  updateSettings: (patch: Partial<AppSettings>) => void
  syncTaskLogs: () => Promise<void>
  resetAll: () => Promise<void>
}

const StoreContext = createContext<AppStore | null>(null)

function mergeImportedState(candidate: AppState): AppState {
  return {
    ...initialState,
    ...candidate,
    version: 1,
    settings: { ...initialState.settings, ...candidate.settings },
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [state, setState] = useState<AppState>(initialState)
  const [hydrated, setHydrated] = useState(false)
  const [databaseStatus, setDatabaseStatus] = useState<AppStore['databaseStatus']>('loading')
  const [lastDraw, setLastDraw] = useState<DrawResult>()

  useEffect(() => {
    let cancelled = false
    setHydrated(false)
    setDatabaseStatus('loading')

    async function hydrate() {
      const local = await loadState(user.id)
      try {
        const remote = await loadRemoteState()
        const selected = mergeImportedState(remote ?? local)
        if (cancelled) return
        setState(selected)
        await saveState(user.id, selected)
        if (!remote) await saveRemoteState(selected)
        if (!cancelled) setDatabaseStatus('saved')
      } catch {
        if (!cancelled) {
          setState(mergeImportedState(local))
          setDatabaseStatus('offline')
        }
      } finally {
        if (!cancelled) setHydrated(true)
      }
    }

    void hydrate()
    return () => { cancelled = true }
  }, [user.id])

  useEffect(() => {
    if (!hydrated) return
    const timeout = window.setTimeout(async () => {
      await saveState(user.id, state)
      setDatabaseStatus('saving')
      try {
        await saveRemoteState(state)
        setDatabaseStatus('saved')
      } catch {
        setDatabaseStatus('offline')
      }
    }, 350)
    return () => window.clearTimeout(timeout)
  }, [state, hydrated, user.id])

  const activeSession = useMemo(
    () => state.sessions.find((session) => session.id === state.activeSessionId),
    [state.sessions, state.activeSessionId],
  )

  const chooseStarter = useCallback((catalogueId: string) => {
    const selected = byId.get(catalogueId)
    if (!selected || selected.category !== 'fish' || selected.unlock !== 'starter') return
    setState((current) => {
      if (current.starterChosen) return current
      const obtainedAt = Date.now()
      return {
        ...current,
        onboardingComplete: true,
        starterChosen: true,
        inventory: [...current.inventory, { itemId: catalogueId, source: 'starter', obtainedAt }],
        fish: [
          ...current.fish,
          { id: createId(), catalogueId, nickname: selected.name, feedTotal: 0, obtainedAt, palette: 0 },
        ],
      }
    })
  }, [])

  const startSession = useCallback((title: string, minutes: number, mode: SessionMode, googleTask?: GoogleTaskRef) => {
    const session = { ...createSession(title, minutes, mode), googleTask }
    setState((current) => ({
      ...current,
      sessions: [session, ...current.sessions],
      activeSessionId: session.id,
    }))
  }, [])

  const updateActive = useCallback((transform: (session: NonNullable<typeof activeSession>) => NonNullable<typeof activeSession>) => {
    setState((current) => ({
      ...current,
      sessions: current.sessions.map((session) =>
        session.id === current.activeSessionId ? transform(session) : session,
      ),
    }))
  }, [])

  const pauseActive = useCallback(() => updateActive((session) => pauseSession(session)), [updateActive])
  const resumeActive = useCallback(() => updateActive((session) => resumeSession(session)), [updateActive])
  const endActive = useCallback(() => updateActive((session) => endSessionEarly(session)), [updateActive])

  const completeActive = useCallback(() => {
    setState((current) => {
      const session = current.sessions.find((entry) => entry.id === current.activeSessionId)
      if (!session || session.status === 'completed' || session.status === 'ended_early') return current
      const completed = completeSession(session)
      const withCompleted = {
        ...current,
        sessions: current.sessions.map((entry) => (entry.id === completed.id ? completed : entry)),
      }
      const rewarded = rewardSession(withCompleted, completed)
      const taskLog = completed.googleTask
        ? {
            sessionId: completed.id,
            task: completed.googleTask,
            minutes: completed.plannedMinutes,
            completedAt: completed.completedAt!,
            attempts: 0,
          }
        : undefined
      return {
        ...rewarded,
        pendingTaskLogs: taskLog ? [...rewarded.pendingTaskLogs, taskLog] : rewarded.pendingTaskLogs,
      }
    })
  }, [])

  const adminCompleteActive = useCallback(() => {
    if (!user.isAdmin) return
    completeActive()
  }, [completeActive, user.isAdmin])

  const dismissCompletion = useCallback(() => setState((current) => ({ ...current, activeSessionId: undefined })), [])

  const feedFish = useCallback((fishId: string, requested?: number) => {
    setState((current) => {
      const target = current.fish.find((entry) => entry.id === fishId)
      if (!target || current.feed < 1) return current
      const needed = feedNeeded(target.feedTotal)
      const amount = Math.min(current.feed, requested ?? (needed || 1), needed || 1)
      if (amount < 1) return current
      return {
        ...current,
        feed: current.feed - amount,
        fish: current.fish.map((entry) =>
          entry.id === fishId ? { ...entry, feedTotal: entry.feedTotal + amount } : entry,
        ),
      }
    })
  }, [])

  const spin = useCallback(() => {
    setState((current) => {
      if (current.tickets < 1) return current
      const result = drawDiscovery(current, catalogue, createId())
      setLastDraw(result)
      if (result.item.category === 'fish' && !result.duplicate) {
        result.nextState = {
          ...result.nextState,
          fish: [
            ...result.nextState.fish,
            {
              id: createId(),
              catalogueId: result.item.id,
              nickname: result.item.name,
              feedTotal: 0,
              obtainedAt: Date.now(),
              palette: 0,
            },
          ],
        }
      }
      return result.nextState
    })
  }, [])

  const togglePlacement = useCallback((itemId: string) => {
    setState((current) => {
      const owned = current.inventory.some((entry) => entry.itemId === itemId)
      const item = byId.get(itemId)
      if (!owned || !item || !['plant', 'decor'].includes(item.category)) return current
      const placed = current.placements.some((entry) => entry.itemId === itemId)
      if (placed) return { ...current, placements: current.placements.filter((entry) => entry.itemId !== itemId) }
      const count = current.placements.length
      return {
        ...current,
        placements: [
          ...current.placements,
          { itemId, x: 12 + ((count * 17) % 76), y: 80 + (count % 3) * 4, scale: 0.85, layer: 'mid' },
        ],
      }
    })
  }, [])

  const movePlacement = useCallback((itemId: string, x: number, y: number) => {
    setState((current) => ({
      ...current,
      placements: current.placements.map((entry) =>
        entry.itemId === itemId
          ? {
              ...entry,
              x: Math.max(4, Math.min(96, x)),
              y: Math.max(24, Math.min(94, y)),
            }
          : entry,
      ),
    }))
  }, [])

  const setBackground = useCallback((itemId: string) => {
    setState((current) =>
      current.inventory.some((entry) => entry.itemId === itemId) && byId.get(itemId)?.category === 'background'
        ? { ...current, activeBackground: itemId }
        : current,
    )
  }, [])

  const arrangeForMe = useCallback(() => {
    setState((current) => {
      const placeable = current.inventory
        .map((entry) => byId.get(entry.itemId))
        .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry && ['plant', 'decor'].includes(entry.category)))
        .slice(0, 8)
      return {
        ...current,
        placements: placeable.map((entry, index) => ({
          itemId: entry.id,
          x: 10 + (index * 80) / Math.max(1, placeable.length - 1),
          y: 81 + (index % 3) * 4,
          scale: entry.category === 'plant' ? 1 : 0.82,
          layer: index % 3 === 0 ? 'back' : index % 3 === 1 ? 'mid' : 'front',
        })),
      }
    })
  }, [])

  const updateSettings = useCallback((patch: Partial<AppSettings>) => {
    setState((current) => ({ ...current, settings: { ...current.settings, ...patch } }))
  }, [])

  const syncTaskLogs = useCallback(async () => {
    if (!isGoogleConnected()) return
    const pending = [...state.pendingTaskLogs]
    for (const operation of pending) {
      try {
        await appendSessionToTask(operation.task, operation.sessionId, operation.minutes, operation.completedAt)
        setState((current) => ({
          ...current,
          pendingTaskLogs: current.pendingTaskLogs.filter((entry) => entry.sessionId !== operation.sessionId),
          sessions: current.sessions.map((session) =>
            session.id === operation.sessionId ? { ...session, taskLogState: 'synced' } : session,
          ),
        }))
      } catch {
        setState((current) => ({
          ...current,
          pendingTaskLogs: current.pendingTaskLogs.map((entry) =>
            entry.sessionId === operation.sessionId ? { ...entry, attempts: entry.attempts + 1 } : entry,
          ),
          sessions: current.sessions.map((session) =>
            session.id === operation.sessionId ? { ...session, taskLogState: 'blocked' } : session,
          ),
        }))
      }
    }
  }, [state.pendingTaskLogs])

  useEffect(() => {
    const sync = () => syncTaskLogs()
    window.addEventListener('online', sync)
    return () => window.removeEventListener('online', sync)
  }, [syncTaskLogs])

  const resetAll = useCallback(async () => {
    await clearState(user.id)
    setState(initialState)
    setLastDraw(undefined)
    try {
      await saveRemoteState(initialState)
      setDatabaseStatus('saved')
    } catch {
      setDatabaseStatus('offline')
    }
  }, [user.id])

  const value = useMemo<AppStore>(
    () => ({
      state,
      hydrated,
      databaseStatus,
      activeSession,
      lastDraw,
      chooseStarter,
      startSession,
      pauseActive,
      resumeActive,
      endActive,
      completeActive,
      adminCompleteActive,
      dismissCompletion,
      feedFish,
      spin,
      clearDraw: () => setLastDraw(undefined),
      togglePlacement,
      movePlacement,
      setBackground,
      arrangeForMe,
      updateSettings,
      syncTaskLogs,
      resetAll,
    }),
    [
      state,
      hydrated,
      databaseStatus,
      activeSession,
      lastDraw,
      chooseStarter,
      startSession,
      pauseActive,
      resumeActive,
      endActive,
      completeActive,
      adminCompleteActive,
      dismissCompletion,
      feedFish,
      spin,
      togglePlacement,
      movePlacement,
      setBackground,
      arrangeForMe,
      updateSettings,
      syncTaskLogs,
      resetAll,
    ],
  )

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

export function useAppStore(): AppStore {
  const context = useContext(StoreContext)
  if (!context) throw new Error('useAppStore must be used inside AppProvider')
  return context
}

export { fishStage }
